(function () {
  'use strict';

  // ── Rule engine ──────────────────────────────────────────────────────────
  // Evaluates declarative JSON rules stored in Supabase (skip_rules, dynamic_content_rules)

  function parseAnswerArray(raw) {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch (_e) { return [raw]; }
  }

  function evalCondition(cond, answers) {
    if (!cond) return true; // null condition = always true (fallback)
    var raw = answers[cond.answer_key] || '';
    var selections = parseAnswerArray(raw);
    var hasMatch = selections.some(function (sel) {
      return cond.values.some(function (v) {
        return sel.indexOf(v) !== -1;
      });
    });
    return cond.match === 'any_contains' ? hasMatch : !hasMatch;
  }

  function evalSkipRules(rules, answers) {
    if (!rules) return false;
    var fn = rules.operator === 'AND'
      ? function (cb) { return rules.conditions.every(cb); }
      : function (cb) { return rules.conditions.some(cb); };
    return fn(function (c) { return evalCondition(c, answers); });
  }

  function evalDynamicContent(rules, answers) {
    if (!rules) return null;
    for (var i = 0; i < rules.length; i++) {
      if (evalCondition(rules[i].condition, answers)) {
        return rules[i].content;
      }
    }
    return null;
  }

  // ── DOM & config ─────────────────────────────────────────────────────────
  var root = document.getElementById('quizz-funnel-root');
  if (!root) return;

  var SUPABASE_URL = root.dataset.supabaseUrl;
  var SUPABASE_ANON_KEY = root.dataset.supabaseAnonKey;
  var N8N_WATER_REPORT_URL = root.dataset.n8nWaterReportUrl;
  var N8N_FUNNEL_COMPLETE_URL = root.dataset.n8nQuizzFunnelCompleteUrl;
  // Optional forced variant — priority: URL param > data-variant (metafield/section setting)
  var _urlParams = new URLSearchParams(window.location.search);
  var _urlVariant = _urlParams.get('variant');
  var _forceTrack = _urlParams.get('track') === '1';
  var _isTestMode = !!_urlVariant && !_forceTrack; // URL param = testing mode, unless track=1
  var FORCED_VARIANT = _urlVariant || root.dataset.variant || null;
  var MARKET = root.dataset.market || null;
  var SHOP_DOMAIN = root.dataset.shop || '';

  var sb = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

  var SUPABASE_INSIGHTS_URL = root.dataset.supabaseInsightsUrl;
  var SUPABASE_INSIGHTS_ANON_KEY = root.dataset.supabaseInsightsAnonKey;
  var sbInsights = (SUPABASE_INSIGHTS_URL && SUPABASE_INSIGHTS_ANON_KEY)
    ? window.supabase.createClient(SUPABASE_INSIGHTS_URL, SUPABASE_INSIGHTS_ANON_KEY)
    : null;

  // ── State ────────────────────────────────────────────────────────────────
  var state = {
    sessionId: null,
    variant: null,          // e.g. 'A', 'B' …
    questions: [],          // fetched from quiz_variant_full view
    thankYou: {},           // fetched from quiz_variant_full view
    currentStep: 0,
    submitting: false,
    answers: {},
    waterReportToken: null, // token returned by n8n water-report workflow
    waterData: null,        // water analysis data for data-driven screens (Variant B)
    waterDataReady: false,  // true when water analysis data has been fetched (or failed)
  };

  var progressBar = document.getElementById('qf-progress-bar');
  var stepContainer = document.getElementById('qf-step-container');

  // ── Progress ─────────────────────────────────────────────────────────────
  function isQuestionStep(q) {
    return q.type !== 'info_screen' && q.type !== 'zipcode_screen';
  }

  // For Variant C: count all non-info steps (including zipcode)
  function isVisibleStep(q) {
    return q.type !== 'info_screen';
  }

  function questionCount() {
    return state.questions.filter(isQuestionStep).length;
  }

  function visibleStepCount() {
    return state.questions.filter(isVisibleStep).length;
  }

  function visibleStepIndex(stepIndex) {
    var count = 0;
    for (var i = 0; i < stepIndex; i++) {
      if (isVisibleStep(state.questions[i])) count++;
    }
    return count;
  }

  function questionIndex(stepIndex) {
    var count = 0;
    for (var i = 0; i < stepIndex; i++) {
      if (state.questions[i] && isQuestionStep(state.questions[i])) count++;
    }
    return count;
  }

  var pctEl = document.getElementById('qf-pct');
  var backBtn = document.getElementById('qf-back');

  function updateProgressBar() {
    var pct = Math.round((state.currentStep / state.questions.length) * 100);
    progressBar.style.width = pct + '%';
    if (pctEl) pctEl.textContent = pct + '%';
    if (backBtn) {
      if (state.currentStep > 0) {
        backBtn.classList.add('is-visible');
      } else {
        backBtn.classList.remove('is-visible');
      }
    }
  }

  if (backBtn) {
    backBtn.addEventListener('click', function () {
      if (state.currentStep > 0) {
        renderStep(state.currentStep - 1, -1);
      }
    });
  }

  // ── Variant config fetching ───────────────────────────────────────────────
  function fetchVariantConfig(variantId, callback) {
    sb.from('quiz_variant_full')
      .select('*')
      .eq('id', variantId)
      .eq('is_active', true)
      .single()
      .then(function (res) {
        if (res.error || !res.data) {
          console.error('[qf] fetchVariantConfig', res.error);
          callback(null);
          return;
        }
        callback(res.data);
      });
  }

  // ── Session management ───────────────────────────────────────────────────
  function createNewSession() {
    if (FORCED_VARIANT) {
      // Forced variant: fetch config directly
      fetchVariantConfig(FORCED_VARIANT, function (config) {
        if (!config) { console.error('[qf] forced variant not found:', FORCED_VARIANT); return; }
        startSessionWithConfig(FORCED_VARIANT, config);
      });
    } else {
      // Random variant: fetch all active variants, pick one
      sb.from('quiz_variants')
        .select('id')
        .eq('is_active', true)
        .then(function (res) {
          if (res.error || !res.data || !res.data.length) {
            console.error('[qf] no active variants', res.error);
            return;
          }
          var keys = res.data.map(function (v) { return v.id; });
          var variant = keys[Math.floor(Math.random() * keys.length)];
          fetchVariantConfig(variant, function (config) {
            if (!config) { console.error('[qf] variant config not found:', variant); return; }
            startSessionWithConfig(variant, config);
          });
        });
    }
  }

  function startSessionWithConfig(variant, config) {
    state.variant = variant;
    state.questions = config.questions;
    state.thankYou = config.thank_you;

    if (_isTestMode) {
      // Test mode: skip all Supabase writes, use fake session
      state.sessionId = 'test-' + Date.now();
      renderStep(0);
      return;
    }

    sb.from('funnel_sessions')
      .insert([{ variant: variant, status: 'started' }])
      .select('id')
      .single()
      .then(function (res) {
        if (res.error) { console.error('[qf] createNewSession', res.error); return; }
        state.sessionId = res.data.id;
        try { localStorage.setItem('qf_session_id', state.sessionId); } catch (e) {}
        try { localStorage.setItem('qf_variant', variant); } catch (e) {}
        if (window.QuizCartSync) window.QuizCartSync.attach();
        insertEvent('session_started');
        renderStep(0);
      });
  }

  function initSession() {
    var stored;
    try { stored = localStorage.getItem('qf_session_id'); } catch (e) {}

    // Test mode: ignore stored sessions, always start fresh without touching localStorage
    if (_isTestMode) {
      stored = null;
    }

    // If a variant is forced and there's an existing session with a different variant, discard it
    if (stored && FORCED_VARIANT) {
      var storedVariant;
      try { storedVariant = localStorage.getItem('qf_variant'); } catch (e) {}
      if (storedVariant && storedVariant !== FORCED_VARIANT) {
        try { localStorage.removeItem('qf_session_id'); } catch (e) {}
        try { localStorage.removeItem('qf_variant'); } catch (e) {}
        stored = null;
      }
    }

    if (stored) {
      sb.from('funnel_sessions')
        .select('id, variant')
        .eq('id', stored)
        .single()
        .then(function (res) {
          if (res.error || !res.data) {
            try { localStorage.removeItem('qf_session_id'); } catch (e) {}
            createNewSession();
          } else {
            state.sessionId = stored;
            var v = res.data.variant || 'A';
            state.variant = v;
            try { localStorage.setItem('qf_variant', v); } catch (e) {}
            fetchVariantConfig(v, function (config) {
              if (!config) {
                // Variant no longer active, start fresh
                try { localStorage.removeItem('qf_session_id'); } catch (e) {}
                createNewSession();
                return;
              }
              state.questions = config.questions;
              state.thankYou = config.thank_you;
              renderStep(state.currentStep);
            });
          }
        });
      return;
    }

    createNewSession();
  }

  // ── Rendering ────────────────────────────────────────────────────────────
  function renderStep(index, direction) {
    direction = direction || 1; // default: forward
    if (index >= state.questions.length) {
      renderLoading();
      // Write completed_at to localStorage first so cart sync includes it
      var completedAt = new Date().toISOString();
      try { localStorage.setItem('qf_completed_at', completedAt); } catch (e) {}
      try { localStorage.setItem('qf_completed_session_id', state.sessionId); } catch (e) {}
      try { localStorage.removeItem('qf_session_id'); } catch (e) {}
      // Sync cart attributes immediately before any async work, then gate redirect
      cartSyncAndComplete();
      return;
    }
    if (index < 0) return; // can't go below 0

    var q = state.questions[index];

    // Evaluate skip rules — advance silently if condition met (in current direction)
    if (q.skip_rules && evalSkipRules(q.skip_rules, state.answers)) {
      renderStep(index + direction, direction);
      return;
    }

    state.currentStep = index;
    updateProgressBar();

    // Show water report preview only on intro screen, hide after
    var previewEl = document.querySelector('.wrp-preview__content');
    var shaderEl = document.querySelector('.wrp-preview__shader');
    var wrapperEl = document.querySelector('.qf-wrapper');
    if (index === 0) {
      if (previewEl) previewEl.style.display = '';
      if (shaderEl) shaderEl.style.display = '';
      if (wrapperEl) wrapperEl.style.background = '#f4f4f4';
    } else {
      if (previewEl) previewEl.style.display = 'none';
      if (shaderEl) shaderEl.style.display = 'none';
      if (wrapperEl) wrapperEl.style.background = '#ffffff';
    }

    var incoming = document.createElement('div');
    incoming.className = 'qf-step qf-step--enter';
    incoming.innerHTML = buildStepHTML(q, index);

    var outgoing = stepContainer.querySelector('.qf-step');
    if (outgoing) {
      outgoing.classList.add('qf-step--exit');
      stepContainer.appendChild(incoming);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          incoming.classList.remove('qf-step--enter');
          incoming.classList.add('qf-step--visible');
        });
      });
      setTimeout(function () {
        if (outgoing.parentNode) outgoing.parentNode.removeChild(outgoing);
      }, 320);
    } else {
      stepContainer.appendChild(incoming);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          incoming.classList.remove('qf-step--enter');
          incoming.classList.add('qf-step--visible');
        });
      });
    }

    bindStepEvents(incoming, q, index);
  }

  function buildStepHTML(q, index) {
    // ── Info screen ────────────────────────────────────────────────────────
    if (q.type === 'info_screen') {
      var displayTitle = q.title;
      var displayBody = q.body;
      if (q.dynamic_content_rules) {
        var dynamic = evalDynamicContent(q.dynamic_content_rules, state.answers);
        if (dynamic && dynamic.title) displayTitle = dynamic.title;
        if (dynamic && dynamic.body) displayBody = dynamic.body;
      }
      var badge = q.badge
        ? '<span class="qf-info-badge">' + esc(q.badge) + '</span>'
        : '';
      var bodyLines = esc(displayBody).replace(/\n/g, '<br>');
      var ctaLabel = q.cta ? esc(q.cta) : 'Continue';

      // ── Data-driven templates (Variant B) ────────────────────────────────
      if (q.data_template === 'loading_screen') {
        var zip = state.answers['zipcode'] || '...';
        return (
          '<div class="qf-card qf-card--data qf-scan">' +
            '<div class="qf-scan__header">' +
              '<p class="qf-scan__title">Scanning water data for <strong class="qf-scan__zip">' + esc(zip) + '</strong>...</p>' +
            '</div>' +
            '<div class="qf-scan__radar"><div class="qf-scan__radar-ping"></div><svg class="qf-scan__radar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg></div>' +
            '<div class="qf-scan__checklist">' +
              '<div class="qf-scan__item" data-scan-step="0"><span class="qf-scan__spinner"></span><span class="qf-scan__check">&#10003;</span> Locating your water supply zone...</div>' +
              '<div class="qf-scan__item" data-scan-step="1"><span class="qf-scan__spinner"></span><span class="qf-scan__check">&#10003;</span> Cross-referencing water reports...</div>' +
              '<div class="qf-scan__item" data-scan-step="2"><span class="qf-scan__spinner"></span><span class="qf-scan__check">&#10003;</span> Identifying contaminants in your area...</div>' +
            '</div>' +
            '<div class="qf-scan__results" data-scan-results style="display:none">' +
              '<div class="qf-scan__stat"><span class="qf-scan__stat-num" data-scan-detected>—</span><span class="qf-scan__stat-label">contaminants detected</span></div>' +
              '<div class="qf-scan__stat"><span class="qf-scan__stat-num qf-scan__stat-num--red" data-scan-above>—</span><span class="qf-scan__stat-label">above health guidelines</span></div>' +
              '<div class="qf-scan__stat"><span class="qf-scan__stat-num qf-scan__stat-num--orange" data-scan-priority>—</span><span class="qf-scan__stat-label">flagged as high priority</span></div>' +
            '</div>' +
            '<p class="qf-scan__cta-hint" data-scan-hint style="display:none">We\u2019ll walk you through what this means \u2014 and what you can do about it.<br><small>Answer a few questions so we can personalize your Water Report.</small></p>' +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue" disabled>' + ctaLabel + '</button>' +
            '</div>' +
          '</div>'
        );
      }

      if (q.data_template === 'exposure_chart') {
        var wd = state.waterData;
        var exposureHtml = '';
        if (wd && wd.exposure && wd.exposure.length > 0) {
          for (var ei = 0; ei < wd.exposure.length; ei++) {
            var ex = wd.exposure[ei];
            var barPct = Math.min(100, Math.max(10, (ex.severity === 'high' ? 75 : 50)));
            exposureHtml +=
              '<div class="qf-expo__row">' +
                '<div class="qf-expo__label">' + esc(ex.name) + '</div>' +
                '<div class="qf-expo__value"><strong>' + esc(String(ex.value.toLocaleString())) + '</strong> ' + esc(ex.unit) + '</div>' +
                '<div class="qf-expo__bar-track"><div class="qf-expo__bar-fill" style="width:0%" data-target-width="' + barPct + '%"></div></div>' +
              '</div>';
          }
        } else {
          exposureHtml = '<p class="qf-expo__empty">Exposure data not available for your area.</p>';
        }
        return (
          '<div class="qf-card qf-card--data qf-expo">' +
            '<div class="qf-expo__header">' +
              '<h2 class="qf-title">' + esc(displayTitle) + '</h2>' +
              (displayBody ? '<p class="qf-expo__subtitle">' + bodyLines + '</p>' : '') +
              '<p class="qf-expo__note">Based on average UK adult consumption, using data from your postcode.</p>' +
            '</div>' +
            '<div class="qf-expo__bars">' + exposureHtml + '</div>' +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue">' + ctaLabel + '</button>' +
            '</div>' +
          '</div>'
        );
      }

      if (q.data_template === 'contaminants_above') {
        var wd2 = state.waterData;
        var zip2 = state.answers['zipcode'] || '';
        var contHtml = '';
        var aboveCount = 0;
        if (wd2 && wd2.contaminantsAbove && wd2.contaminantsAbove.length > 0) {
          aboveCount = wd2.contaminantsAbove.length;
          for (var ci = 0; ci < wd2.contaminantsAbove.length; ci++) {
            var ct = wd2.contaminantsAbove[ci];
            var multLabel = ct.multiplier >= 100 ? Math.round(ct.multiplier) + 'x' : ct.multiplier.toFixed(1) + 'x';
            var barW = Math.min(100, Math.max(5, ct.multiplier * 10));
            contHtml +=
              '<div class="qf-contam__row">' +
                '<div class="qf-contam__name">' + esc(ct.name) + '</div>' +
                '<div class="qf-contam__mult">' + esc(multLabel) + '</div>' +
                '<div class="qf-contam__bar-track"><div class="qf-contam__bar-fill" style="width:0%" data-target-width="' + barW + '%"></div></div>' +
              '</div>';
          }
        } else {
          contHtml = '<p style="color:rgba(255,255,255,0.6);text-align:center;">No contaminants above health guidelines detected.</p>';
        }
        var dynTitle = (aboveCount > 0 ? '<span class="qf-contam__count">' + aboveCount + '</span> ' : '') + 'Contaminants Above Health Guidelines';
        if (zip2) dynTitle += '<br><span class="qf-contam__zip">in ' + esc(zip2) + '</span>';
        return (
          '<div class="qf-card qf-card--data qf-contam">' +
            '<h2 class="qf-title">' + dynTitle + '</h2>' +
            '<div class="qf-contam__legend"><span class="qf-contam__legend-line"></span> Health guideline</div>' +
            '<div class="qf-contam__list">' + contHtml + '</div>' +
            '<div class="qf-contam__note">' +
              '<p><strong>Your water is legal. That doesn\u2019t mean it\u2019s clean.</strong> UK limits are designed to make water drinkable \u2014 not optimal. Independent health organisations like the EWG and WHO set thresholds up to 100x stricter.</p>' +
              '<small>Your full breakdown will be in your Water Report.</small>' +
            '</div>' +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue">' + ctaLabel + '</button>' +
            '</div>' +
          '</div>'
        );
      }

      if (q.data_template === 'comparison_table') {
        var checkSvg = '<svg class="qf-cmp__icon qf-cmp__icon--check" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4" stroke="#16A772" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        var crossSvg = '<svg class="qf-cmp__icon qf-cmp__icon--cross" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="#E04040" stroke-width="2" stroke-linecap="round"/></svg>';
        var warnSvg = '<svg class="qf-cmp__icon qf-cmp__icon--warn" viewBox="0 0 16 16" fill="none"><path d="M8 1L1 14h14L8 1z" stroke="#E8A200" stroke-width="1.5" stroke-linejoin="round"/><path d="M8 6v4M8 12h.01" stroke="#E8A200" stroke-width="1.5" stroke-linecap="round"/></svg>';
        var rows = [
          ['Chlorine taste', warnSvg, checkSvg + ' 98-99%'],
          ['Lead', crossSvg, checkSvg + ' 95-99%'],
          ['PFAS', crossSvg, checkSvg + ' Up to 99%'],
          ['Fluoride', crossSvg, checkSvg + ' 90-98%'],
          ['Microplastics', crossSvg, checkSvg + ' 99%'],
          ['Pesticides', crossSvg, checkSvg + ' Yes'],
          ['Bacteria (E. coli)', crossSvg, checkSvg + ' 99%'],
          ['Preserves minerals', warnSvg, checkSvg + ' Yes'],
        ];
        var tableRows = '';
        for (var ri = 0; ri < rows.length; ri++) {
          tableRows +=
            '<tr class="qf-cmp__row">' +
              '<td class="qf-cmp__cell qf-cmp__cell--name">' + rows[ri][0] + '</td>' +
              '<td class="qf-cmp__cell qf-cmp__cell--jug">' + rows[ri][1] + '</td>' +
              '<td class="qf-cmp__cell qf-cmp__cell--puure">' + rows[ri][2] + '</td>' +
            '</tr>';
        }
        return (
          '<div class="qf-card qf-card--data qf-cmp">' +
            '<h2 class="qf-title">' + esc(displayTitle) + '</h2>' +
            '<table class="qf-cmp__table">' +
              '<thead><tr>' +
                '<th class="qf-cmp__th">Contaminant</th>' +
                '<th class="qf-cmp__th">Standard Jug</th>' +
                '<th class="qf-cmp__th qf-cmp__th--puure">Multi-Barrier</th>' +
              '</tr></thead>' +
              '<tbody>' + tableRows + '</tbody>' +
            '</table>' +
            '<p class="qf-cmp__source">Source: NSF-42/53 certification standards and independent lab testing.</p>' +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue">' + ctaLabel + '</button>' +
            '</div>' +
          '</div>'
        );
      }

      var afterCard = '';
      if (q.key === 'intro') {
        return (
          '<div class="qf-card qf-card--info qf-card--intro">' +
            '<h2 class="qf-title" style="text-align:center;">What\u2019s Really in Your Tap Water?</h2>' +
            '<p class="qf-info-body" style="text-align:center;color:#6b7280;">Enter your postcode. See the contaminants detected in your area.</p>' +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue">Check My Postcode</button>' +
            '</div>' +
            '<p class="qf-intro-sub-cta">Less than 2 minutes \u00b7 100% free</p>' +
            '<a href="https://puurefilters.com/products/tap-system" class="qf-intro-view-product">Skip to shop</a>' +
          '</div>'
        );
      }
      if (q.key === 'info_contaminants') {
        afterCard =
          '<div class="qf-source">' +
            '<img class="qf-source__logo" src="https://cdn.shopify.com/s/files/1/0897/2963/0553/files/who-logo-png-transparent-1536x1536.png?v=1775547781" alt="WHO" loading="lazy">' +
            '<p class="qf-source__text">Source: European Environment Agency / WHO Guidelines</p>' +
          '</div>';
      }
      if (q.key === 'info_drink_more') {
        var tpStar = '<svg viewBox="0 0 11 10" focusable="false" aria-hidden="true"><path d="M2.477 9.972c.06-.196.115-.376.167-.556.319-1.056.641-2.116.961-3.172.018-.064.003-.092-.042-.128-.968-.748-1.932-1.5-2.896-2.252a.09.09 0 01-.056-.048c.026-.02.048-.012.07-.012 1.195 0 2.393 0 3.587 0 .06 0 .082-.02.1-.08.368-1.212.735-2.424 1.102-3.636.008-.024.015-.044.026-.084.019.064.034.112.048.156.36 1.192.724 2.384 1.08 3.576.019.056.041.072.096.072 1.195 0 2.393 0 3.587 0 .022 0 .048-.008.078.012-1.621 1.264-3.242 2.528-4.863 3.792 0 0 0 .008 0 .012l.17-.044c.627-.172 1.254-.34 1.881-.516.04-.012.052 0 .063.036.285.952.571 1.9.856 2.848.004.016.008.028.015.056-.145-.112-.278-.216-.412-.324-.853-.672-1.706-1.34-2.559-2.016-.041-.032-.063-.024-.1 0-.968.752-1.936 1.504-2.9 2.256-.019.012-.037.028-.068.048z" fill="#fff"/></svg>';
        var tpStars5 = '<span class="qf-tp__sq is-on">' + tpStar + '</span><span class="qf-tp__sq is-on">' + tpStar + '</span><span class="qf-tp__sq is-on">' + tpStar + '</span><span class="qf-tp__sq is-on">' + tpStar + '</span><span class="qf-tp__sq is-on">' + tpStar + '</span>';
        afterCard =
          '<div class="qf-reviews">' +
            '<div class="qf-review">' +
              '<div class="qf-review__head">' +
                '<div class="qf-review__avatar">ST</div>' +
                '<div class="qf-review__who"><div class="qf-review__name">Sarah T.</div><div class="qf-review__meta">GB 1 review</div></div>' +
                '<div class="qf-review__date">Mar 14, 2026</div>' +
              '</div>' +
              '<div class="qf-review__rating">' + tpStars5 + '</div>' +
              '<p class="qf-review__text">The water from the Puure filter tastes just like fresh spring water. Crisp, clean \u2014 I actually enjoy drinking tap water now.</p>' +
              '<div class="qf-review__actions"><span class="qf-review__action"><svg viewBox="0 0 24 24" focusable="false"><path d="M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.3l1-4.7.03-.32c0-.4-.17-.8-.44-1.07L13 1 6.6 7.4C6.2 7.8 6 8.3 6 8.9V19c0 1.1.9 2 2 2h8c.8 0 1.5-.5 1.8-1.2l3-7.1c.1-.2.2-.5.2-.7v-2z"/></svg>Useful</span><span class="qf-review__action"><svg viewBox="0 0 24 24" focusable="false"><path d="M18 16.1c-.8 0-1.5.3-2 .8L8.9 12.7c.1-.2.1-.5.1-.7s0-.5-.1-.7l7-4.1c.5.5 1.2.8 2 .8 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3c0 .2 0 .5.1.7l-7 4.1C7.5 9.3 6.8 9 6 9c-1.7 0-3 1.3-3 3s1.3 3 3 3c.8 0 1.5-.3 2-.8l7.1 4.2c-.1.2-.1.4-.1.6 0 1.6 1.3 2.9 2.9 2.9S21 22.6 21 21s-1.3-2.9-3-2.9z"/></svg>Share</span></div>' +
            '</div>' +
            '<div class="qf-review">' +
              '<div class="qf-review__head">' +
                '<div class="qf-review__avatar" style="background:#d7e8f8;">EW</div>' +
                '<div class="qf-review__who"><div class="qf-review__name">Emma W.</div><div class="qf-review__meta">GB 1 review</div></div>' +
                '<div class="qf-review__date">Jan 27, 2026</div>' +
              '</div>' +
              '<div class="qf-review__rating">' + tpStars5 + '</div>' +
              '<p class="qf-review__text">Didn\u2019t expect this \u2014 my tea and coffee taste so much better since we got the Puure filter. The whole family noticed it on day one.</p>' +
              '<div class="qf-review__actions"><span class="qf-review__action"><svg viewBox="0 0 24 24" focusable="false"><path d="M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.3l1-4.7.03-.32c0-.4-.17-.8-.44-1.07L13 1 6.6 7.4C6.2 7.8 6 8.3 6 8.9V19c0 1.1.9 2 2 2h8c.8 0 1.5-.5 1.8-1.2l3-7.1c.1-.2.2-.5.2-.7v-2z"/></svg>Useful</span><span class="qf-review__action"><svg viewBox="0 0 24 24" focusable="false"><path d="M18 16.1c-.8 0-1.5.3-2 .8L8.9 12.7c.1-.2.1-.5.1-.7s0-.5-.1-.7l7-4.1c.5.5 1.2.8 2 .8 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3c0 .2 0 .5.1.7l-7 4.1C7.5 9.3 6.8 9 6 9c-1.7 0-3 1.3-3 3s1.3 3 3 3c.8 0 1.5-.3 2-.8l7.1 4.2c-.1.2-.1.4-.1.6 0 1.6 1.3 2.9 2.9 2.9S21 22.6 21 21s-1.3-2.9-3-2.9z"/></svg>Share</span></div>' +
            '</div>' +
          '</div>';
      }
      var showResultVideo = (q.key === 'info_tap_result');
      if (showResultVideo) {
        // Split body on double newlines: first paragraph next to video, rest below full-width
        var bodyParts = String(displayBody).split(/\n\s*\n/);
        var firstPara = esc(bodyParts.shift() || '').replace(/\n/g, '<br>');
        var restPara = esc(bodyParts.join('\n\n') || '').replace(/\n/g, '<br>');
        return (
          '<div class="qf-card qf-card--info qf-card--with-video">' +
            badge +
            '<div class="qf-card__split">' +
              '<div class="qf-card__split-text">' +
                '<h2 class="qf-title">' + esc(displayTitle) + '</h2>' +
                (firstPara ? '<p class="qf-info-body">' + firstPara + '</p>' : '') +
              '</div>' +
              '<div class="qf-card__split-media">' +
                '<div class="qf-tap-video" data-tap-video-slot></div>' +
              '</div>' +
            '</div>' +
            (restPara ? '<p class="qf-info-body qf-info-body--full">' + restPara + '</p>' : '') +
            '<div class="qf-footer">' +
              '<button type="button" class="qf-btn-continue">' + ctaLabel + '</button>' +
            '</div>' +
          '</div>' +
          afterCard
        );
      }
      return (
        '<div class="qf-card qf-card--info">' +
        badge +
        '<h2 class="qf-title">' + esc(displayTitle) + '</h2>' +
        '<p class="qf-info-body">' + bodyLines + '</p>' +
        '<div class="qf-footer">' +
        '<button type="button" class="qf-btn-continue">' + ctaLabel + '</button>' +
        '</div>' +
        '</div>' +
        afterCard
      );
    }

    // ── Zipcode screen ─────────────────────────────────────────────────────
    if (q.type === 'zipcode_screen') {
      var _isC = state.variant === 'C' || state.variant === 'D';
      var _zipTitle = _isC
        ? 'What\u2019s really in your tap water?'
        : 'What\u2019s your postcode?';
      var _zipSubtitle = _isC
        ? '<p class="qf-subtitle">Your water quality depends on where you live. Enter your postcode \u2014 we\u2019ll pull the data.</p>'
        : '';
      var _zipDisclaimer = _isC
        ? 'Takes under 30sec \u00b7 Free personalised Water Report'
        : 'Takes 60\u201390 seconds. Personalized results based on your postcode.<br>Some contaminants are legally permitted \u2014 but still exceed stricter health-based recommendations.';
      var _zipWhyTitle = _isC ? 'WHY YOUR POSTCODE MATTERS' : 'WHY WE ASK';
      var _zipWhyText = _isC
        ? 'Contaminants like PFAS, chlorine byproducts, and pesticides vary dramatically by area. Some are legally permitted \u2014 but still exceed stricter health-based recommendations. Your postcode lets us pull the exact data for your supply.'
        : 'Water quality varies drastically by area. Contaminants like PFAS and pesticides may be legally permitted in your supply \u2014 but still exceed stricter health-based recommendations. Your postcode lets us pull the exact data for your area.';
      var _zipWhy =
        '<div class="qf-zipcode-why">' +
        '<p class="qf-zipcode-why__title">\u2192 ' + _zipWhyTitle + '</p>' +
        '<p class="qf-zipcode-why__text">' + _zipWhyText + '</p>' +
        '</div>';
      var _zipWhyInside = _isC ? _zipWhy : '';
      var _zipWhyOutside = _isC ? '' : _zipWhy;
      var _zipStepLabel = _isC
        ? '<div class="qf-step-label">Question ' + (visibleStepIndex(index) + 1) + ' of ' + visibleStepCount() + '</div>'
        : '';
      return (
        '<div class="qf-card qf-card--zipcode">' +
        _zipStepLabel +
        '<h2 class="qf-title">' + _zipTitle + '</h2>' +
        _zipSubtitle +
        '<div class="qf-body">' +
        '<div class="qf-input-wrap">' +
        '<input class="qf-input" type="text" placeholder="e.g. SW1A 1AA" autocomplete="off" />' +
        '<p class="qf-input-error" style="display:none"></p>' +
        '</div>' +
        '</div>' +
        '<div class="qf-footer">' +
        '<button type="button" class="qf-btn-continue" disabled>Check My Area</button>' +
        '<p class="qf-zipcode-disclaimer">' + _zipDisclaimer + '</p>' +
        '</div>' +
        _zipWhyInside +
        '</div>' +
        _zipWhyOutside
      );
    }

    // ── Regular question ───────────────────────────────────────────────────
    var qNum = questionIndex(index) + 1;
    var qTotal = questionCount();
    var subtitle = q.subtitle
      ? '<p class="qf-subtitle">' + esc(q.subtitle) + '</p>'
      : '';

    var body = '';

    if (q.type === 'multi_input') {
      body = '<div class="qf-input-group">';
      q.fields.forEach(function (f) {
        body +=
          '<div class="qf-input-wrap">' +
          '<input class="qf-input" type="' + escAttr(f.inputType || 'text') + '" placeholder="' + escAttr(f.placeholder || '') + '" data-field-key="' + escAttr(f.key) + '" autocomplete="off" />' +
          '</div>';
      });
      body += '</div>';
    }

    if (q.type === 'text_input' || q.type === 'number_input') {
      var itype = q.type === 'number_input' ? 'number' : (q.inputType || 'text');
      body =
        '<div class="qf-input-wrap">' +
        '<input class="qf-input" type="' + itype + '" placeholder="' + escAttr(q.placeholder || '') + '" autocomplete="off" />' +
        (q.validate === 'postcode' ? '<p class="qf-input-error" style="display:none"></p>' : '') +
        '</div>';
    }

    if (q.type === 'single_choice') {
      var scHasImages = q.option_images && q.option_images.some(function (img) { return !!img; });
      body = '<div class="qf-choices' + (scHasImages ? ' qf-choices--images' : '') + '" data-multi="false">';
      q.options.forEach(function (opt, oi) {
        var scOptImg = (q.option_images && q.option_images[oi]) ? '<img class="qf-choice__img" src="' + escAttr(q.option_images[oi]) + '" alt="" loading="lazy">' : '';
        body +=
          '<button type="button" class="qf-choice' + (scOptImg ? ' qf-choice--has-img' : '') + '" data-value="' + escAttr(opt) + '">' +
          '<span class="qf-choice__check"></span>' +
          (scOptImg ? scOptImg : '') +
          '<span class="qf-choice__label">' + esc(opt) + '</span>' +
          '</button>';
      });
      body += '</div>';
    }

    if (q.type === 'multiple_choice') {
      var hasImages = q.option_images && q.option_images.some(function (img) { return !!img; });
      body = '<div class="qf-choices' + (hasImages ? ' qf-choices--images' : '') + '" data-multi="true">';
      q.options.forEach(function (opt, oi) {
        var optImg = (q.option_images && q.option_images[oi]) ? '<img class="qf-choice__img" src="' + escAttr(q.option_images[oi]) + '" alt="" loading="lazy">' : '';
        body +=
          '<button type="button" class="qf-choice' + (optImg ? ' qf-choice--has-img' : '') + '" data-value="' + escAttr(opt) + '">' +
          '<span class="qf-choice__check"></span>' +
          (optImg ? optImg : '') +
          '<span class="qf-choice__label">' + esc(opt) + '</span>' +
          '</button>';
      });
      body += '</div>';
    }

    var qAfterCard = '';
    if (q.key === 'previous_solution') {
      qAfterCard =
        '<div class="qf-zipcode-why">' +
        '<p class="qf-zipcode-why__title">\u2192 WHY WE ASK</p>' +
        '<p class="qf-zipcode-why__text">Not all solutions are equal. Pitcher filters typically operate at around 100 microns \u2014 fine for chlorine, but not enough for PFAS, microplastics, or heavy metals. Bottled water avoids tap contaminants but can contain up to 240,000 microplastic particles per litre (Columbia University, 2024) \u2014 and costs roughly 30x more. Your answer helps us show how Puure compares to what you\u2019ve tried.</p>' +
        '</div>';
    }
    if (q.key === 'faucet_incompatible') {
      qAfterCard =
        '<div class="qf-zipcode-why">' +
        '<p class="qf-zipcode-why__title">\u2192 WHY WE ASK</p>' +
        '<p class="qf-zipcode-why__text">Most of these tap types work best with an under-sink installation rather than a tap-mounted filter. Don\u2019t worry \u2014 we have a solution for every setup.</p>' +
        '</div>';
    }
    if (q.key === 'physical_signs') {
      qAfterCard =
        '<div class="qf-zipcode-why">' +
        '<p class="qf-zipcode-why__title">\u2192 WHY WE ASK</p>' +
        '<p class="qf-zipcode-why__text">These are common signs of hard water, which affects around 60% of UK households. It impacts everything \u2014 from the taste of your water and tea, to your skin, hair, and appliances. Your Water Report will flag if your area is affected.</p>' +
        '</div>';
    }

    var qStepLabel = '';
    if (state.variant === 'C' || state.variant === 'D') {
      qStepLabel = '<div class="qf-step-label">Question ' + (visibleStepIndex(index) + 1) + ' of ' + visibleStepCount() + '</div>';
    }

    return (
      '<div class="qf-card">' +
      qStepLabel +
      '<h2 class="qf-title">' + esc(q.title) + '</h2>' +
      subtitle +
      '<div class="qf-body">' + body + '</div>' +
      '<div class="qf-footer">' +
      '<button type="button" class="qf-btn-continue" disabled>Continue</button>' +
      '</div>' +
      '</div>' +
      qAfterCard
    );
  }

  function bindStepEvents(el, q, index) {
    var btn = el.querySelector('.qf-btn-continue');

    // ── Data-driven screens (Variant B) ──────────────────────────────────
    if (q.data_template === 'loading_screen') {
      btn.disabled = true;
      // Animate the scan checklist
      var scanSteps = el.querySelectorAll('[data-scan-step]');
      var stepDelay = 1500;
      for (var si = 0; si < scanSteps.length; si++) {
        (function (step, delay) {
          setTimeout(function () {
            step.classList.add('qf-scan__item--done');
          }, delay);
        })(scanSteps[si], stepDelay * (si + 1));
      }
      // Poll for waterDataReady
      function checkScanReady() {
        if (state.waterDataReady) {
          var results = el.querySelector('[data-scan-results]');
          var hint = el.querySelector('[data-scan-hint]');
          if (results) {
            var wd = state.waterData;
            if (wd && wd.summary) {
              el.querySelector('[data-scan-detected]').textContent = wd.summary.totalContaminants || '—';
              el.querySelector('[data-scan-above]').textContent = wd.summary.aboveGuidelines || '—';
              el.querySelector('[data-scan-priority]').textContent = wd.summary.highPriority || '—';
            }
            results.style.display = '';
          }
          if (hint) hint.style.display = '';
          // Wait a beat for the user to read the results before enabling button
          setTimeout(function () { btn.disabled = false; }, 1200);
        } else {
          setTimeout(checkScanReady, 500);
        }
      }
      // Start checking after the checklist animation completes
      setTimeout(checkScanReady, stepDelay * scanSteps.length + 500);
      btn.addEventListener('click', function () {
        btn.disabled = true;
        saveAnswer(q.key, 'viewed', index).then(function () { renderStep(index + 1); });
      });
      return;
    }

    if (q.data_template === 'exposure_chart' || q.data_template === 'contaminants_above') {
      // Animate bars after a short delay
      setTimeout(function () {
        var fills = el.querySelectorAll('[data-target-width]');
        for (var fi = 0; fi < fills.length; fi++) {
          (function (fill, delay) {
            setTimeout(function () {
              fill.style.width = fill.getAttribute('data-target-width');
            }, delay);
          })(fills[fi], fi * 150);
        }
      }, 300);
      btn.addEventListener('click', function () {
        btn.disabled = true;
        saveAnswer(q.key, 'viewed', index).then(function () { renderStep(index + 1); });
      });
      return;
    }

    if (q.data_template === 'comparison_table') {
      btn.addEventListener('click', function () {
        btn.disabled = true;
        saveAnswer(q.key, 'viewed', index).then(function () { renderStep(index + 1); });
      });
      return;
    }

    // ── Info screen: save 'viewed' then continue ───────────────────────────
    if (q.type === 'info_screen') {
      // Move the correct preloaded video (tap or undersink) into its slot
      var videoSlot = el.querySelector('[data-tap-video-slot]');
      if (videoSlot) {
        // Determine which video to use based on the displayed title
        var stepTitle = el.querySelector('.qf-title');
        var isTapResult = stepTitle && stepTitle.textContent.indexOf('Tap System') !== -1;
        var preloadedVideo = document.getElementById(isTapResult ? 'qf-preload-tap-video' : 'qf-preload-undersink-video');
        if (preloadedVideo) {
          videoSlot.appendChild(preloadedVideo);
          preloadedVideo.muted = true;
          var playPromise = preloadedVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {
              var retry = function () {
                preloadedVideo.play().catch(function () {});
                document.removeEventListener('touchstart', retry);
                document.removeEventListener('click', retry);
              };
              document.addEventListener('touchstart', retry, { once: true });
              document.addEventListener('click', retry, { once: true });
            });
          }
        }
      }
      btn.disabled = true;
      btn.addEventListener('click', function () {
        btn.disabled = true;
        saveAnswer(q.key, 'viewed', index).then(function () {
          renderStep(index + 1);
        });
      });
      btn.disabled = false;
      return;
    }

    // ── Zipcode screen ─────────────────────────────────────────────────────
    if (q.type === 'zipcode_screen') {
      var zcInput = el.querySelector('.qf-input');
      zcInput.addEventListener('input', function () {
        btn.disabled = zcInput.value.trim().length < 2;
        var errEl = el.querySelector('.qf-input-error');
        if (errEl) errEl.style.display = 'none';
      });
      zcInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !btn.disabled) btn.click();
      });
      setTimeout(function () { zcInput.focus(); }, 340);
      btn.addEventListener('click', function () {
        if (state.submitting) return;
        var value = zcInput.value.trim();
        if (!value) return;
        btn.disabled = true;
        btn.textContent = 'Checking...';
        validatePostcode(value, el, function (valid) {
          if (!valid) {
            btn.disabled = false;
            btn.textContent = 'Analyze My Water';
            return;
          }
          if (state.variant !== 'D') triggerWaterReport(value);
          handleContinue(q, value, btn, index);
        });
      });
      return;
    }

    // ── Multi-input ────────────────────────────────────────────────────────
    if (q.type === 'multi_input') {
      var multiInputs = el.querySelectorAll('.qf-input');
      multiInputs.forEach(function (inp) {
        inp.addEventListener('input', function () {
          btn.disabled = !validateStep(q, getStepValue(el, q));
        });
        inp.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && !btn.disabled) btn.click();
        });
      });
      setTimeout(function () { if (multiInputs[0]) multiInputs[0].focus(); }, 340);
    }

    // ── Text / number input ────────────────────────────────────────────────
    if (q.type === 'text_input' || q.type === 'number_input') {
      var input = el.querySelector('.qf-input');
      input.addEventListener('input', function () {
        var valid = validateStep(q, input.value.trim());
        btn.disabled = !valid;
        if (q.validate === 'postcode') {
          var errEl = el.querySelector('.qf-input-error');
          if (errEl) errEl.style.display = 'none';
        }
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !btn.disabled) btn.click();
      });
      setTimeout(function () { input.focus(); }, 340);
    }

    // ── Single choice ──────────────────────────────────────────────────────
    if (q.type === 'single_choice') {
      var choices = el.querySelectorAll('.qf-choice');
      choices.forEach(function (c) {
        c.addEventListener('click', function () {
          choices.forEach(function (x) { x.classList.remove('is-selected'); });
          c.classList.add('is-selected');
          btn.disabled = false;
          // Inline hint for first option on avoided_tap_water
          if (q.key === 'avoided_tap_water') {
            var hintId = 'qf-avoided-hint';
            var hint = document.getElementById(hintId);
            var choicesWrap = c.closest('.qf-choices');
            if (c === choices[0] || c === choices[1]) {
              if (!hint) {
                hint = document.createElement('p');
                hint.id = hintId;
                hint.className = 'qf-choice-hint';
                hint.textContent = 'You\u2019re not alone \u2014 that\u2019s one of the most common reasons people switch to filtered water.';
                choicesWrap.parentNode.insertBefore(hint, choicesWrap.nextSibling);
              }
              hint.style.display = '';
            } else if (hint) {
              hint.style.display = 'none';
            }
          }
        });
      });
    }

    // ── Multiple choice ────────────────────────────────────────────────────
    if (q.type === 'multiple_choice') {
      var mcChoices = el.querySelectorAll('.qf-choice');
      mcChoices.forEach(function (c) {
        c.addEventListener('click', function () {
          c.classList.toggle('is-selected');
          btn.disabled = el.querySelectorAll('.qf-choice.is-selected').length === 0;
          // Inline hint for "Tastes fine to me" on tap_water_taste question
          if (q.key === 'tap_water_taste') {
            var firstOpt = mcChoices[0];
            var hintId = 'qf-taste-hint';
            var hint = document.getElementById(hintId);
            var mcChoicesWrap = firstOpt.closest('.qf-choices');
            if (firstOpt.classList.contains('is-selected')) {
              if (!hint) {
                hint = document.createElement('p');
                hint.id = hintId;
                hint.className = 'qf-choice-hint';
                hint.textContent = 'Good to hear \u2014 but many harmful contaminants like PFAS and pesticides are tasteless and odourless. Your Water Report will reveal what\u2019s undetectable.';
                mcChoicesWrap.parentNode.insertBefore(hint, mcChoicesWrap.nextSibling);
              }
              hint.style.display = '';
            } else if (hint) {
              hint.style.display = 'none';
            }
          }
        });
      });
    }

    // ── Continue button ────────────────────────────────────────────────────
    btn.addEventListener('click', function () {
      if (state.submitting) return;
      var value = getStepValue(el, q);
      if (!validateStep(q, value)) return;

      if (q.validate === 'postcode') {
        btn.disabled = true;
        btn.textContent = 'Checking...';
        validatePostcode(value, el, function (valid) {
          if (!valid) {
            btn.disabled = false;
            btn.textContent = 'Continue';
            return;
          }
          if (state.variant !== 'D') triggerWaterReport(value);
          handleContinue(q, value, btn, index);
        });
        return;
      }

      handleContinue(q, value, btn, index);
    });
  }

  // ── Validation ───────────────────────────────────────────────────────────
  function validatePostcode(postcode, el, callback) {
    var normalized = postcode.replace(/\s+/g, '');

    if (MARKET === 'US') {
      var valid = /^\d{5}(-\d{4})?$/.test(normalized);
      if (!valid) {
        var errEl = el.querySelector('.qf-input-error');
        if (errEl) {
          errEl.textContent = 'This ZIP code does not appear to be valid. Please check and try again.';
          errEl.style.display = 'block';
        }
      }
      callback(valid);
      return;
    }

    // Default: UK validation via postcodes.io
    fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(normalized) + '/validate')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.result === true) {
          callback(true);
        } else {
          var errEl = el.querySelector('.qf-input-error');
          if (errEl) {
            errEl.textContent = 'This postcode does not appear to be valid. Please check and try again.';
            errEl.style.display = 'block';
          }
          callback(false);
        }
      })
      .catch(function (err) {
        console.error('[qf] validatePostcode', err);
        callback(true);
      });
  }

  function getStepValue(el, q) {
    if (q.type === 'multi_input') {
      var vals = {};
      el.querySelectorAll('.qf-input').forEach(function (inp) {
        vals[inp.dataset.fieldKey] = inp.value.trim();
      });
      return vals;
    }
    if (q.type === 'text_input' || q.type === 'number_input') {
      return el.querySelector('.qf-input').value.trim();
    }
    if (q.type === 'single_choice') {
      var s = el.querySelector('.qf-choice.is-selected');
      return s ? s.dataset.value : null;
    }
    if (q.type === 'multiple_choice') {
      var selected = [];
      el.querySelectorAll('.qf-choice.is-selected').forEach(function (s) { selected.push(s.dataset.value); });
      return selected.length > 0 ? selected.join(',') : null;
    }
    return null;
  }

  function validateStep(q, value) {
    if (value === null || value === undefined || value === '') return false;
    if (q.type === 'multi_input') {
      if (typeof value !== 'object') return false;
      return q.fields.every(function (f) {
        if (!value[f.key] || value[f.key].length === 0) return false;
        if (f.inputType === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value[f.key]);
        return true;
      });
    }
    if (q.type === 'number_input') return /^\d+$/.test(value);
    if (q.inputType === 'email' || q.key === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    return true;
  }

  // ── Data persistence ─────────────────────────────────────────────────────
  function handleContinue(q, value, btn, index) {
    state.submitting = true;
    btn.disabled = true;
    btn.textContent = 'Saving...';

    state.answers[q.key] = value;

    var sessionUpdates = {};
    if (q.type === 'multi_input') {
      if (value.first_name) sessionUpdates.first_name = value.first_name;
      if (value.last_name) sessionUpdates.last_name = value.last_name;
      if (value.email) sessionUpdates.email = value.email;
      if (value.phone_number) sessionUpdates.phone_number = value.phone_number;
    }
    if (q.key === 'email') sessionUpdates.email = value;

    var chain;
    if (q.type === 'multi_input') {
      chain = Promise.all(q.fields.map(function (f) {
        return saveAnswer(f.key, value[f.key], index);
      })).then(function () { return insertEvent('answer_submitted'); });
    } else {
      chain = saveAnswer(q.key, value, index)
        .then(function () { return insertEvent('answer_submitted'); });
    }

    if (Object.keys(sessionUpdates).length > 0) {
      chain = chain.then(function () {
        return sb.from('funnel_sessions').update(sessionUpdates).eq('id', state.sessionId);
      });
    }

    if (q.key === 'email' && state.variant === 'D') {
      triggerWaterReport(state.answers['zipcode']);
      var _uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      var _validSessionId = _uuidRegex.test(state.sessionId) ? state.sessionId : null;
      var _emailPayload = { session_id: _validSessionId, email: value };
      chain = chain.then(function () {
        return sb.from('variant_d_email_captures')
          .insert([_emailPayload])
          .then(function (res) {
            if (res.error) console.error('[qf] variant_d_email_captures (main)', res.error);
          });
      }).then(function () {
        if (!sbInsights) return;
        return sbInsights.from('variant_d_email_captures')
          .insert([_emailPayload])
          .then(function (res) {
            if (res.error) console.error('[qf] variant_d_email_captures (insights)', res.error);
          });
      });
    }

    chain
      .then(function () {
        state.submitting = false;
        renderStep(index + 1);
      })
      .catch(function (err) {
        console.error('[qf] handleContinue', err);
        state.submitting = false;
        btn.disabled = false;
        btn.textContent = 'Continue';
      });
  }

  function saveAnswer(questionKey, answerValue, questionIndex) {
    if (_isTestMode) return Promise.resolve();
    return sb.from('funnel_answers')
      .insert([{
        session_id: state.sessionId,
        question_key: questionKey,
        question_index: questionIndex,
        answer_value: String(answerValue),
      }])
      .then(function (res) {
        if (res.error) console.error('[qf] saveAnswer', res.error);
      });
  }

  function insertEvent(eventName) {
    if (_isTestMode) return Promise.resolve();
    return sb.from('funnel_events')
      .insert([{ session_id: state.sessionId, event_name: eventName }])
      .then(function (res) {
        if (res.error) console.error('[qf] insertEvent', res.error);
      });
  }

  // ── External integrations ─────────────────────────────────────────────────
  function triggerWaterReport(zipcode) {
    if (!N8N_WATER_REPORT_URL || _isTestMode) return;
    var contact = state.answers['contact'] || {};
    var _waterEmail = state.answers['email'] || contact.email || null;
    state.waterDataReady = false;

    sb.from('water_reports')
      .insert([{
        session_id: state.sessionId,
        zipcode: zipcode,
        status: 'in_progress',
      }])
      .select('id')
      .single()
      .then(function (res) {
        if (res.error) { console.error('[qf] water_reports insert', res.error); return; }
        var reportId = res.data.id;

        fetch(N8N_WATER_REPORT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: state.variant === 'D' ? 'quizz-funnel-variant-d' : 'quizz-funnel',
            quizz_funnel_session_id: state.sessionId,
            water_report_id: reportId,
            zipcode: zipcode,
            email: _waterEmail,
            phone_number: contact.phone_number || null,
            market: MARKET,
          }),
        })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.token) state.waterReportToken = data.token;
            // Update water_reports in Supabase
            sb.from('water_reports')
              .update({
                status: data.status || 'success',
                token: data.token || null,
                result: data.result || null,
                updated_at: new Date().toISOString(),
              })
              .eq('id', reportId)
              .then(function (upd) {
                if (upd.error) console.error('[qf] water_reports update', upd.error);
              });
            // Chain: fetch water analysis data for data-driven screens
            if (data.token && SHOP_DOMAIN) {
              return fetch('/apps/quizz-funnel?action=water-analysis&token=' + encodeURIComponent(data.token) + '&shop=' + encodeURIComponent(SHOP_DOMAIN))
                .then(function (r2) { return r2.json(); })
                .then(function (waterData) {
                  if (waterData && !waterData.error) {
                    state.waterData = waterData;
                  }
                  state.waterDataReady = true;
                });
            } else {
              state.waterDataReady = true;
            }
          })
          .catch(function (err) {
            console.error('[qf] triggerWaterReport fetch', err);
            state.waterDataReady = true; // unblock loading screen even on error
            sb.from('water_reports')
              .update({
                status: 'error',
                error_message: err.message || 'fetch failed',
                updated_at: new Date().toISOString(),
              })
              .eq('id', reportId)
              .then(function () {});
          });
      });
  }

  function triggerFunnelComplete(answers) {
    if (!N8N_FUNNEL_COMPLETE_URL || _isTestMode) return Promise.resolve(null);
    return fetch(N8N_FUNNEL_COMPLETE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quizz_funnel_session_id: state.sessionId,
        water_report_token: state.waterReportToken || null,
        answers: answers,
        email: answers['email'] || null,
        market: MARKET,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { return data.token || null; })
      .catch(function (err) {
        console.error('[qf] triggerFunnelComplete', err);
        return null;
      });
  }

  function completeSession() {
    if (_isTestMode) return Promise.resolve(null);
    return sb.from('funnel_sessions')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('id', state.sessionId)
      .then(function (res) {
        if (res.error) console.error('[qf] completeSession', res.error);
        insertEvent('session_completed');
        return triggerFunnelComplete(state.answers);
      });
  }

  // ── Analysis screen ───────────────────────────────────────────────────────
  // Gate state: redirect only when BOTH 5s timer AND backend call are done.
  var _analysisDone = { animation: false, backend: false, token: null, redirected: false };

  function _attemptRedirect() {
    if (_analysisDone.redirected) return;
    if (!_analysisDone.animation || !_analysisDone.backend) return;
    _analysisDone.redirected = true;
    if (_analysisDone.token) {
      window.location.href = '/apps/quizz-funnel?token=' + encodeURIComponent(_analysisDone.token);
    } else {
      // Backend returned no token — show error on analysis screen
      var errEl = document.getElementById('qf-analysis-error');
      var screen = document.getElementById('qf-analysis-screen');
      if (errEl) errEl.style.display = 'block';
      if (screen) screen.classList.remove('qf-analysis--loading');
    }
  }

  function generateAnalysisLogs(answers) {
    var zip = (answers['zipcode'] || '').toUpperCase() || 'your area';
    var taste = answers['tap_water_taste'] || '';
    var concern = answers['main_concern'] || '';

    // Message 3 — personalized by taste
    var msg3;
    if (taste.indexOf('chlorine') !== -1) {
      msg3 = 'Analyzing chlorine taste indicators';
    } else if (taste.indexOf('metallic') !== -1 || taste.indexOf('Metallic') !== -1) {
      msg3 = 'Analyzing metallic taste indicators';
    } else {
      msg3 = 'Analyzing water profile indicators';
    }

    // Message 4 — personalized by concern
    var msg4;
    if (concern.indexOf('PFAS') !== -1) {
      msg4 = 'Scanning regional PFAS contamination data';
    } else if (concern.indexOf('pesticide') !== -1 || concern.indexOf('Pesticide') !== -1) {
      msg4 = 'Scanning agricultural contamination data';
    } else {
      msg4 = 'Running contaminant profile analysis';
    }

    return [
      'Locating your water supply zone near ' + zip,
      'Identifying local water treatment infrastructure',
      msg3,
      msg4,
      'Matching optimal filtration technology',
      'Preparing your personalized water report',
    ];
  }

  function _startStatusCycle() {
    var container = document.getElementById('qf-analysis-status-list');
    if (!container) return;
    var logs = generateAnalysisLogs(state.answers);
    var i = 0;

    function addPair() {
      if (i >= logs.length) return;
      // Add 2 lines at a time so both columns fill simultaneously
      var count = Math.min(2, logs.length - i);
      var lines = [];
      for (var j = 0; j < count; j++) {
        var line = document.createElement('div');
        line.className = 'qf-analysis-log-line qf-analysis-log-line--hidden';
        line.innerHTML = '<span class="qf-log-text">' + esc(logs[i + j]) + '</span>';
        container.appendChild(line);
        lines.push(line);
      }
      i += count;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          lines.forEach(function (l) { l.classList.remove('qf-analysis-log-line--hidden'); });
        });
      });
    }

    addPair();
    var interval = setInterval(function () {
      if (i >= logs.length) { clearInterval(interval); return; }
      addPair();
    }, (state.variant === 'C' || state.variant === 'D') ? 800 : 1500);
  }

  function _startProgressBar(durationMs) {
    var bar = document.getElementById('qf-analysis-progress-fill');
    if (!bar) return;
    // Force reflow at 0% then animate to 100%
    bar.style.transition = 'none';
    bar.style.width = '0%';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        bar.style.transition = 'width ' + durationMs + 'ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        bar.style.width = '92%'; // stop at 92% — completes to 100% on redirect
      });
    });
  }

  function _initMap(zipcode) {
    var mapEl = document.getElementById('qf-analysis-map');
    if (!mapEl || !zipcode) return;
    var iframe = document.createElement('iframe');
    iframe.className = 'qf-analysis-map-iframe';
    iframe.src = 'https://www.google.com/maps?q=' + encodeURIComponent(zipcode + ', UK') + '&output=embed&z=14&t=k';
    iframe.loading = 'lazy';
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
    iframe.setAttribute('allowfullscreen', '');
    mapEl.appendChild(iframe);
  }

  var _isPreviewMode = false;

  function _showClaimButton() {
    var bar = document.getElementById('qf-analysis-progress-fill');
    var progressWrap = document.querySelector('.qf-analysis-progress-wrap');
    // Complete the bar to 100% instantly then fade out the wrap
    if (bar) {
      bar.style.transition = 'width 0.3s ease';
      bar.style.width = '100%';
    }
    var _fastExit = state.variant === 'C' || state.variant === 'D';
    setTimeout(function () {
      if (progressWrap) {
        progressWrap.style.transition = 'opacity ' + (_fastExit ? '0.2' : '0.4') + 's ease';
        progressWrap.style.opacity = '0';
      }
      setTimeout(function () {
        if (progressWrap) progressWrap.style.display = 'none';
        _attemptRedirect();
      }, _fastExit ? 200 : 400);
    }, _fastExit ? 200 : 300);
  }

  function _startAnalysisTimer() {
    var duration = (state.variant === 'C' || state.variant === 'D') ? 5000 : 10000;
    setTimeout(function () {
      if (!_isPreviewMode) _analysisDone.animation = true;
      _showClaimButton();
      if (!_isPreviewMode) _attemptRedirect();
    }, duration);
  }

  function renderLoading() {
    progressBar.style.width = '100%';
    var progressWrap = document.getElementById('qf-progress-wrap');
    if (progressWrap) progressWrap.style.display = 'none';

    // Center the screen on the loading view
    if (root) root.classList.add('qf-root--analysis');
    window.scrollTo({ top: root ? root.getBoundingClientRect().top + window.scrollY : 0, behavior: 'smooth' });

    // Reset gate
    _analysisDone.animation = false;
    _analysisDone.backend = false;
    _analysisDone.token = null;
    _analysisDone.redirected = false;

    var zipcode = state.answers['zipcode'] || '';

    var el = document.createElement('div');
    el.className = 'qf-step qf-step--enter';
    el.id = 'qf-analysis-screen';
    el.classList.add('qf-analysis--loading');
    el.innerHTML =
      '<div class="qf-analysis-card">' +

        // ── Header ──────────────────────────────────────────────────────────
        '<div class="qf-analysis-header">' +
          '<p class="qf-analysis-eyebrow">Water Quality Analysis</p>' +
          '<h2 class="qf-analysis-title">Analyzing your local<br>water quality</h2>' +
          (zipcode
            ? '<div class="qf-analysis-postcode-wrap">' +
                '<span class="qf-analysis-postcode-dot"></span>' +
                '<span class="qf-analysis-postcode">' + esc(zipcode.toUpperCase()) + '</span>' +
              '</div>'
            : '') +
        '</div>' +

        // ── Map + radar ──────────────────────────────────────────────────────
        '<div class="qf-analysis-map-wrap">' +
          '<div class="qf-analysis-map-glow"></div>' +
          '<div class="qf-analysis-map-circle">' +
            '<div id="qf-analysis-map" class="qf-analysis-map-container"></div>' +
            '<div class="qf-analysis-radar">' +
              '<div class="qf-radar-ring qf-radar-ring--1"></div>' +
              '<div class="qf-radar-ring qf-radar-ring--2"></div>' +
              '<div class="qf-radar-ring qf-radar-ring--3"></div>' +
              '<div class="qf-radar-sweep"></div>' +
              '<div class="qf-radar-crosshair qf-radar-crosshair--h"></div>' +
              '<div class="qf-radar-crosshair qf-radar-crosshair--v"></div>' +
              '<div class="qf-radar-center"></div>' +
            '</div>' +
          '</div>' +
        '</div>' +

        // ── Progress bar ─────────────────────────────────────────────────────
        '<div class="qf-analysis-progress-wrap">' +
          '<div class="qf-analysis-progress-track">' +
            '<div id="qf-analysis-progress-fill" class="qf-analysis-progress-fill"></div>' +
          '</div>' +
          '<span class="qf-analysis-progress-label">Analyzing\u2026</span>' +
        '</div>' +
        // ── Log lines ────────────────────────────────────────────────────────
        '<div id="qf-analysis-status-list" class="qf-analysis-status-list"></div>' +

        // ── Error ────────────────────────────────────────────────────────────
        '<div id="qf-analysis-error" class="qf-analysis-error" style="display:none">' +
          '<p>Something went wrong generating your report.</p>' +
          '<button type="button" class="qf-btn-continue" id="qf-analysis-retry">Try again</button>' +
        '</div>' +

      '</div>';

    var outgoing = stepContainer.querySelector('.qf-step');
    if (outgoing) {
      outgoing.classList.add('qf-step--exit');
      stepContainer.appendChild(el);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.remove('qf-step--enter');
          el.classList.add('qf-step--visible');
          // Timer starts only after the screen is visually painted
          _startAnalysisTimer();
        });
      });
      setTimeout(function () {
        if (outgoing.parentNode) outgoing.parentNode.removeChild(outgoing);
      }, 320);
    } else {
      stepContainer.appendChild(el);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.remove('qf-step--enter');
          el.classList.add('qf-step--visible');
          // Timer starts only after the screen is visually painted
          _startAnalysisTimer();
        });
      });
    }

    // Start UX sequences
    var _loadingDuration = (state.variant === 'C' || state.variant === 'D') ? 5000 : 10000;
    _startStatusCycle();
    _startProgressBar(_loadingDuration);
    _initMap(zipcode);

    // Retry button (timer is started separately via _startAnalysisTimer)
    el.addEventListener('click', function (e) {
      if (e.target && e.target.id === 'qf-analysis-retry') {
        var errEl = document.getElementById('qf-analysis-error');
        if (errEl) errEl.style.display = 'none';
        _analysisDone.redirected = false;
        _analysisDone.backend = false;
        _analysisDone.token = null;
        // Re-trigger the backend call only
        cartSyncAndComplete();
      }
    });
  }

  // Extracted so the retry button can re-call it
  function cartSyncAndComplete() {
    var doComplete = function () {
      var cartSyncPromise = window.QuizCartSync ? window.QuizCartSync.attach() : Promise.resolve();
      cartSyncPromise.then(function () {
        return completeSession();
      }).then(function (metaobjectToken) {
        _analysisDone.token = metaobjectToken || null;
        _analysisDone.backend = true;
        _attemptRedirect();
      }).catch(function (err) {
        console.error('[qf] cartSyncAndComplete failed:', err);
        _analysisDone.token = null;
        _analysisDone.backend = true;
        _attemptRedirect();
      });
    };

    // For variant D, water report is triggered at email step — wait for it before completing
    if (state.variant === 'D' && !state.waterDataReady) {
      var _poll = setInterval(function () {
        if (state.waterDataReady) {
          clearInterval(_poll);
          doComplete();
        }
      }, 300);
    } else {
      doComplete();
    }
  }

  // ── Thank you screen (fallback if no token returned) ─────────────────────
  function renderThankYou() {
    progressBar.style.width = '100%';

    var ty = state.thankYou || {};
    var el = document.createElement('div');
    el.className = 'qf-step qf-step--enter';
    el.innerHTML =
      '<div class="qf-card qf-card--thankyou">' +
      '<div class="qf-thankyou-icon">&#10003;</div>' +
      '<h2 class="qf-title">' + esc(ty.title || "You're all set.") + '</h2>' +
      '<p class="qf-subtitle">' + esc(ty.subtitle || '') + '</p>' +
      '</div>';

    var outgoing = stepContainer.querySelector('.qf-step');
    if (outgoing) {
      outgoing.classList.add('qf-step--exit');
      stepContainer.appendChild(el);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.remove('qf-step--enter');
          el.classList.add('qf-step--visible');
        });
      });
      setTimeout(function () {
        if (outgoing.parentNode) outgoing.parentNode.removeChild(outgoing);
      }, 320);
    } else {
      stepContainer.appendChild(el);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.remove('qf-step--enter');
          el.classList.add('qf-step--visible');
        });
      });
    }
  }

  // ── Utilities ────────────────────────────────────────────────────────────
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    // ?preview=loading → show analysis screen directly, skipping the quiz
    if (new URLSearchParams(window.location.search).get('preview') === 'loading') {
      _isPreviewMode = true;
      var p = new URLSearchParams(window.location.search);

      // Zipcode
      state.answers['zipcode'] = p.get('zip') || 'SW1A 1AA';

      // Water source — values: public | well | unknown
      var srcMap = {
        'public': '🚰 Public water supply',
        'well':   '🏡 Private well / borehole',
        'unknown': '❓ Not sure'
      };
      if (p.get('src')) state.answers['water_source'] = srcMap[p.get('src')] || p.get('src');

      // Taste — values: chlorine | metallic | odor | neutral
      var tasteMap = {
        'chlorine': '🧪 Noticeable chlorine taste',
        'metallic': '🔩 Metallic taste',
        'odor':     '🦨 Strong odor or smell',
        'neutral':  '✅ Neutral / Normal taste'
      };
      if (p.get('taste')) state.answers['tap_water_taste'] = tasteMap[p.get('taste')] || p.get('taste');

      // Feeling — values: bad | tolerate
      var feelingMap = {
        'bad':     "🤢 I don't enjoy drinking it",
        'tolerate': '😕 I tolerate it'
      };
      if (p.get('feeling')) state.answers['tap_water_feeling'] = feelingMap[p.get('feeling')] || p.get('feeling');

      // Physical signs — values: limescale | skin | glass (comma-separated)
      var signsMap = {
        'limescale': '⚪ Limescale buildup in kettle or appliances',
        'skin':      '🧴 Dry skin after showering',
        'glass':     '🍽️ White spots on glassware'
      };
      if (p.get('signs')) {
        state.answers['physical_signs'] = p.get('signs').split(',').map(function (s) {
          return signsMap[s.trim()] || s.trim();
        });
      }

      // Hardness — values: veryhard | moderate | slight | soft
      var hardnessMap = {
        'veryhard': '🪨 Very hard',
        'moderate': '🧊 Moderately hard',
        'slight':   '💧 Slightly hard',
        'soft':     '🌊 Soft'
      };
      if (p.get('hardness')) state.answers['water_hardness'] = hardnessMap[p.get('hardness')] || p.get('hardness');

      // Main concern — values: pfas | pesticides | metals | unsure
      var concernMap = {
        'pfas':       '🧬 PFAS ("forever chemicals")',
        'pesticides': '🌿 Pesticides',
        'metals':     '⚙️ Heavy metals',
        'unsure':     '❓ Not sure'
      };
      if (p.get('concern')) state.answers['main_concern'] = concernMap[p.get('concern')] || p.get('concern');

      progressBar.style.width = '100%';
      renderLoading();
      // No backend call, no redirect in preview mode
      return;
    }
    // If variant is forced via URL param, skip the API fetch entirely
    if (_urlVariant) {
      initSession();
    } else if (SHOP_DOMAIN) {
      var _initDone = false;
      function _safeInit() {
        if (_initDone) return;
        _initDone = true;
        initSession();
      }
      var _timeout = setTimeout(_safeInit, 3000);
      fetch('/apps/quizz-funnel?action=get-forced-variant&shop=' + encodeURIComponent(SHOP_DOMAIN))
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.variant) FORCED_VARIANT = data.variant;
          else FORCED_VARIANT = null;
        })
        .catch(function () { /* use data-variant fallback */ })
        .then(function () { clearTimeout(_timeout); _safeInit(); });
    } else {
      initSession();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
